/**
 * biomes.ts — G2 · THE BIOME LAYER (grounded-world-plan.md §3 G2)
 *
 * The macro layer of the grounded world: large seeded regions that tell every
 * other system WHERE it is — ground wash + litter (mapChunks bake), forest
 * corridor tightness (forest.ts carve), lamp spacing (paths.ts). Dylan's
 * mockup brief: biomes separated by lush impassable trees, seamless
 * transitions, per-biome terrain feel.
 *
 * v1 ships Dead Lot + Forest (no new art — tint/density dials over existing
 * surfaces). Reserved for later phases: neighborhood (G3), corruption (G4),
 * military (G5) — they ADD members here, they don't restructure.
 *
 * Determinism law: pure functions of (x, y, seed) — no module state, no
 * anchors, nothing run-mutable. Everything that samples this layer agrees
 * forever, across chunks, across systems, across reloads of the same seed.
 * (The G8 lesson: veinsForChunk turned out anchor-state-dependent and it made
 * verification hell — this layer stays pure on purpose.)
 *
 * Border blending: biomeWeightsAt returns normalized weights over a ~600wu
 * band (warped so borders are organic, never straight). Consumers that need
 * smoothness (bake wash, carve mul, lamp mul) use the weighted blend — there
 * is no hard line anywhere except the debug overlay.
 */

/** kill switch — false restores pre-G2 behavior wholesale */
export const MAP_BIOMES = true;

export type BiomeId = 'deadlot' | 'forest' | 'neighborhood';

export interface BiomeDef {
  /** ground wash painted over the base in the chunk bake (a=0 → none) */
  wash: { r: number; g: number; b: number; a: number };
  /** litter flecks per world-aligned 128wu bake cell */
  litter: number;
  /** litter fleck color: base RGB + per-fleck jitter (G3 N1 — forest drops
   *  leaves, a neighborhood drops paper and trash; one renderer, per-biome
   *  palette) */
  litterBase: [number, number, number];
  litterJitter: [number, number, number];
  /** lamp spacing multiplier (>1 = sparser, darker biome) */
  lampSpacingMul: number;
  /** forest carve-radius multiplier — CLAMPED [0.85, 1.25] by consumers:
   *  <1 tighter corridors, >1 opener. Small on purpose: no existing trail
   *  may pinch shut (movement-feel guardrail in the G2 charter). */
  forestCarveMul: number;
  /** prop-scatter density hook — consumed by later phases, 1 today */
  propScatterMul: number;
}

export const BIOME_DEFS: Record<BiomeId, BiomeDef> = {
  // the world as shipped — wash-free so the flag-off and deadlot looks match
  deadlot: {
    wash: { r: 0, g: 0, b: 0, a: 0 },
    litter: 0,
    litterBase: [34, 44, 18], litterJitter: [20, 22, 14],
    lampSpacingMul: 1,
    forestCarveMul: 1.12,   // dead lots read OPEN — corridors breathe
    propScatterMul: 1,
  },
  // deep woods: mossy-dark wash, leaf litter, sparse lamps, tight corridors
  forest: {
    wash: { r: 26, g: 38, b: 22, a: 0.10 },
    litter: 3,
    litterBase: [34, 44, 18], litterJitter: [20, 22, 14],
    lampSpacingMul: 1.55,
    forestCarveMul: 0.88,
    propScatterMul: 1,
  },
  // G3 N1 — suburban-dead: cracked-pavement grey wash, paper/trash flecks,
  // denser lamps (streets), corridors slightly opener than woods
  neighborhood: {
    wash: { r: 52, g: 48, b: 44, a: 0.08 },
    litter: 2,
    litterBase: [150, 145, 132], litterJitter: [40, 38, 30],
    lampSpacingMul: 0.8,
    forestCarveMul: 1.10,
    propScatterMul: 1,
  },
};

// ── seeded macro regions ─────────────────────────────────────────────────────
// One site per BIOME_CELL lattice cell, weighted biome roll per site; the
// point's biome = nearest site by noise-warped distance (organic borders).
const BIOME_CELL = 7680;   // 23.76 — shrunk from 12288 so neighborhoods/variety come OFTEN, not after a marathon (Dylan: "don't run forever to find a neighborhood")
/** full crossfade band width (wu) — half each side of the equidistance line */
const BLEND_WU = 600;
/** border warp amplitude (wu) — bends the equidistance line organically */
const WARP_WU = 900;
const WARP_CELL = 1400;

function _h32(a: number, b: number, seed: number, salt: number): number {
  let h = (Math.imul(a | 0, 0x9E3779B1) ^ Math.imul(b | 0, 0x85EBCA6B)
    ^ Math.imul(seed | 0, 0xC2B2AE35) ^ Math.imul(salt | 0, 0x27D4EB2F)) >>> 0;
  h = Math.imul(h ^ (h >>> 13), 0x165667B1) >>> 0;
  return ((h ^ (h >>> 15)) >>> 0) / 0xFFFFFFFF;
}
function _smooth(t: number): number { return t * t * (3 - 2 * t); }
function _warpNoise(x: number, y: number, seed: number): number {
  const gx = Math.floor(x / WARP_CELL), gy = Math.floor(y / WARP_CELL);
  const fx = _smooth(x / WARP_CELL - gx), fy = _smooth(y / WARP_CELL - gy);
  const a = _h32(gx, gy, seed, 11), b = _h32(gx + 1, gy, seed, 11);
  const c = _h32(gx, gy + 1, seed, 11), d = _h32(gx + 1, gy + 1, seed, 11);
  return (a * (1 - fx) + b * fx) * (1 - fy) + (c * (1 - fx) + d * fx) * fy;
}

/** biome roll per lattice site — 23.76 NEIGHBORHOOD-DOMINANT (Dylan's living-world
 *  brief: the map should read like real suburbs — mostly lived-in blocks, with
 *  woods as the SEAMS between them and barren dead-lots as texture, not the rule).
 *  neighborhood ~52% (the default place) · forest ~26% (green buffers + treeline
 *  seams) · deadlot ~22% (barren spots). Junkyards/crash/graves still come from
 *  the non-neighborhood pockets in paths.ts, so variety stays. */
function _siteBiome(cx: number, cy: number, seed: number): BiomeId {
  const r = _h32(cx, cy, seed, 22);
  return r < 0.52 ? 'neighborhood' : r < 0.78 ? 'forest' : 'deadlot';
}

interface SiteDist { id: BiomeId; d: number }

function _siteDistances(x: number, y: number, seed: number): SiteDist[] {
  // ONE warp sample for the query point (not per site): warping the point,
  // not the metric, keeps the nearest-site ordering consistent and cheap
  const wx = x + (_warpNoise(x, y, seed) - 0.5) * 2 * WARP_WU;
  const wy = y + (_warpNoise(x + 31337, y - 71993, seed) - 0.5) * 2 * WARP_WU;
  // 3×3 window centered on the WARPED point (Mariah fleet fix, folded into
  // N1 so world layouts reshuffle exactly once): near a cell edge the warp
  // can push the point far enough that its true nearest site sits outside a
  // window centered on the RAW point.
  const ccx = Math.floor(wx / BIOME_CELL), ccy = Math.floor(wy / BIOME_CELL);
  const out: SiteDist[] = [];
  for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) {
    const cx = ccx + dx, cy = ccy + dy;
    const sx = (cx + 0.15 + _h32(cx, cy, seed, 33) * 0.7) * BIOME_CELL;
    const sy = (cy + 0.15 + _h32(cx, cy, seed, 44) * 0.7) * BIOME_CELL;
    out.push({ id: _siteBiome(cx, cy, seed), d: Math.hypot(wx - sx, wy - sy) });
  }
  out.sort((a, b) => a.d - b.d);
  return out;
}

/** hard biome at a point (logic/debug — renderers use biomeWeightsAt) */
export function biomeAt(x: number, y: number, seed: number): BiomeId {
  if (!MAP_BIOMES) return 'deadlot';
  return _siteDistances(x, y, seed)[0].id;
}

/**
 * Normalized biome weights at a point — the crossfade primitive. Away from
 * borders this is [{id, w:1}]; inside the ~600wu band it blends ALL distinct
 * biomes in reach by their own distance-to-equidistance (N1 gate fix: the old
 * two-way blend flipped rival IDENTITY at triple junctions carrying ~0.5
 * weight — 15 hard seams measured; per-id falloff is continuous everywhere
 * because a min over distances is continuous when sites swap order). In the
 * two-biome case this reduces EXACTLY to the shipped G2 curve.
 */
export function biomeWeightsAt(x: number, y: number, seed: number): Array<{ id: BiomeId; w: number }> {
  if (!MAP_BIOMES) return [{ id: 'deadlot', w: 1 }];
  const sites = _siteDistances(x, y, seed);
  const near = sites[0];
  // nearest distance per DISTINCT id (sites are sorted → first hit wins)
  const best = new Map<BiomeId, number>();
  for (const s of sites) if (!best.has(s.id)) best.set(s.id, s.d);
  if (best.size === 1) return [{ id: near.id, w: 1 }];
  // signed distance to the equidistance line ≈ (d - dNear) / 2
  const tOf = (d: number) => Math.min(1, Math.max(0, (d - near.d) / 2 / (BLEND_WU / 2)));
  // the NEAREST rival drives the near biome's own weight (exact G2 curve);
  // every distinct rival contributes by its own falloff
  let tRival = 1;
  for (const [id, d] of Array.from(best)) if (id !== near.id) tRival = Math.min(tRival, tOf(d));
  const raw: Array<{ id: BiomeId; w: number }> = [{ id: near.id, w: 0.5 + 0.5 * _smooth(tRival) }];
  for (const [id, d] of Array.from(best)) {
    if (id === near.id) continue;
    const t = tOf(d);
    if (t >= 1) continue;
    raw.push({ id, w: 0.5 - 0.5 * _smooth(t) });
  }
  if (raw.length === 1) return [{ id: near.id, w: 1 }];
  const sum = raw.reduce((a, r) => a + r.w, 0);
  return raw.map(r => ({ id: r.id, w: r.w / sum }));
}

/** weighted blend helpers — smooth across borders by construction */
function _blend(x: number, y: number, seed: number, pick: (d: BiomeDef) => number): number {
  let v = 0;
  for (const { id, w } of biomeWeightsAt(x, y, seed)) v += pick(BIOME_DEFS[id]) * w;
  return v;
}

export function biomeCarveMulAt(x: number, y: number, seed: number): number {
  if (!MAP_BIOMES) return 1;
  return Math.min(1.25, Math.max(0.85, _blend(x, y, seed, d => d.forestCarveMul)));
}

export function biomeLampSpacingMulAt(x: number, y: number, seed: number): number {
  if (!MAP_BIOMES) return 1;
  return _blend(x, y, seed, d => d.lampSpacingMul);
}
